/**
 * Tendly gamification — app-specific domain layer.
 *
 * The core (`engine.ts`) is agnostic; here we define WHAT rewards asset care:
 * logging maintenance (service_record) and adding an asset. A streak of "care
 * days". Gamification is **positive and optional**;
 * private leagues by default. It does NOT use costs (`cost_cents`): it only counts events.
 *
 * LOCAL-FIRST source: the live `service_record`/`asset` tables (not
 * `activity_recent`). Pure and deterministic reducer → instant feedback,
 * also for guests without sync.
 */
import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { applyEvents, type GamificationConfig, initialState } from "./engine";
import { computeStreak, dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Streak line: "days with a maintenance logged" (care). */
export const STREAK_CARE = "care";

/** `GamificationConfig` for tendly. Fixed XP per event; never reads costs. */
export const TENDLY_GAMIFICATION: GamificationConfig = {
  xp: {
    "service.logged": 12,
    "asset.added": 5,
  },
  streakLines: {
    "service.logged": [STREAK_CARE],
  },
  counters: {
    "service.logged": ["services"],
    "asset.added": ["assets"],
  },
  badges: [
    { id: "first-service", title: "Primer mantenimiento", when: counterAtLeast("services", 1) },
    { id: "ten-services", title: "Cuidador", when: counterAtLeast("services", 10) },
    {
      id: "fifty-services",
      title: "Guardián",
      when: counterAtLeast("services", 50),
      bonusXp: 25,
    },
    {
      id: "century-services",
      title: "Maestro del cuidado",
      when: counterAtLeast("services", 100),
      bonusXp: 50,
    },
    { id: "first-asset", title: "Primer activo", when: counterAtLeast("assets", 1) },
    { id: "collection", title: "Colección", when: counterAtLeast("assets", 5), bonusXp: 15 },
    {
      id: "week-streak",
      title: "Semana al día",
      when: streakAtLeast(STREAK_CARE, 7),
      bonusXp: 40,
    },
    {
      id: "month-streak",
      title: "Mes impecable",
      when: streakAtLeast(STREAK_CARE, 30),
      bonusXp: 100,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-1",
      title: "Registra un mantenimiento hoy",
      period: "daily",
      goals: [{ metric: "service.logged", target: 1 }],
      bonusXp: 10,
    },
    {
      id: "weekly-3",
      title: "3 mantenimientos esta semana",
      period: "weekly",
      goals: [{ metric: "service.logged", target: 3 }],
      bonusXp: 30,
    },
  ],
};

/** Badge catalog (for rendering badges not yet earned, in gray). */
export const TENDLY_BADGES = TENDLY_GAMIFICATION.badges ?? [];

// ── LOCAL-FIRST derivation from `service_record` / `asset` ────────────────

/** Minimum row of `service_record` (a completed maintenance). */
export interface GamifiableServiceRecord {
  performed_at: string | null;
}

/**
 * Derives game events from live rows. Each maintenance emits
 * `service.logged` (day = `performed_at`); each asset emits `asset.added`
 * (no streak line → its day is irrelevant, it only counts for XP/badge).
 * Pure and deterministic. It does NOT read costs.
 */
export function tendlyRowsToGameEvents(
  services: readonly GamifiableServiceRecord[],
  assetCount = 0,
): GameEvent[] {
  const events: GameEvent[] = [];
  for (let i = 0; i < assetCount; i += 1) {
    events.push({ type: "asset.added", at: 0, day: dayKeyUTC(0) });
  }
  for (const s of services) {
    const at = s.performed_at ? Date.parse(s.performed_at) : Number.NaN;
    if (Number.isNaN(at)) {
      continue;
    }
    events.push({ type: "service.logged", at, day: dayKeyUTC(at) });
  }
  events.sort((a, b) => a.at - b.at);
  return events;
}

/**
 * Recomputes the player state from `service_record`/`asset` (local-first).
 * Reanchors the care streak to the real "today". Pure.
 */
export function computePlayerFromTendly(
  services: readonly GamifiableServiceRecord[],
  assetCount = 0,
  todayKey: string = dayKeyUTC(Date.now()),
): { state: PlayerState; events: number } {
  const events = tendlyRowsToGameEvents(services, assetCount);
  const { state } = applyEvents(initialState(), events, TENDLY_GAMIFICATION);
  const active = state.activeDays[STREAK_CARE] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_CARE] = streak;
  }
  return { state, events: events.length };
}
